/**
 * Tipos y utilidades del procesador de FACTURAS EMITIDAS desde PDF.
 * Convierte lo extraído por la IA (+ el maestro de clientes) en las filas que
 * consume `buildIssuedSuenlaceFile`.
 */
import { parseNumericLike } from "@/lib/numberFormatting";
import { isGenericCustomerAccount, type IssuedInvoiceRow } from "@/lib/a3ecoExportIssued";
import { normalizeNif, type CustomerMasterEntry } from "@/lib/customerMaster";

export interface IssuedVatLine {
  taxableBase: number;
  vatRate: number;
  vatAmount: number;
  reRate?: number;
  reAmount?: number;
}

export interface IssuedProcessedFile {
  id: string;
  file: File;
  /** Anexos vinculados (no modifican la factura). */
  attachments?: File[];
  date: string;
  series: string;
  invoiceNumber: string;
  customerName: string;
  customerTaxId: string;
  customerAccount: string;
  salesAccount: string;
  postalCode: string;
  vatLines: IssuedVatLine[];
  irpfRate: number;
  irpfAmount: number;
  totalAmount: number;
  paymentMethod: string;
  /** true cuando el cliente ya existe en el maestro y se han aplicado sus cuentas. */
  masterApplied: boolean;
  /** Factura de abono / rectificativa: los importes van en negativo. */
  isCreditNote?: boolean;
  /** Indicios de abono encontrados en el texto del documento. */
  creditNoteHints?: string[];
  /** "strong" = indicio inequívoco, "weak" = dudoso, "none" = ninguno. */
  creditNoteHintLevel?: "strong" | "weak" | "none";
  /** El usuario cambió el signo a mano: no volver a avisar de abono. */
  creditNoteReviewed?: boolean;
  reviewed?: boolean;
  /**
   * true cuando el usuario ha corregido a mano datos del cliente/cuentas:
   * la recarga del maestro no debe sobrescribir sus cambios.
   */
  manuallyEdited?: boolean;
}

export interface IssuedFailedFile {
  id: string;
  file: File;
  reason: string;
  kind: "rejected" | "error";
}

export const emptyVatLine = (): IssuedVatLine => ({ taxableBase: 0, vatRate: 21, vatAmount: 0 });

/** Aplica al documento las cuentas del maestro de clientes (si el NIF existe). */
export const applyCustomerMaster = (
  doc: IssuedProcessedFile,
  master: Map<string, CustomerMasterEntry>,
): IssuedProcessedFile => {
  const entry = master.get(normalizeNif(doc.customerTaxId));
  if (!entry) return { ...doc, masterApplied: false };
  return {
    ...doc,
    customerName: entry.description || doc.customerName,
    customerAccount: entry.accountCode || doc.customerAccount,
    salesAccount: entry.salesAccount || doc.salesAccount,
    postalCode: entry.postalCode || doc.postalCode,
    masterApplied: true,
  };
};

/** Suma de bases / cuotas del desglose. */
export const docTotals = (doc: IssuedProcessedFile) => {
  const base = doc.vatLines.reduce((s, l) => s + parseNumericLike(l.taxableBase), 0);
  const vat = doc.vatLines.reduce((s, l) => s + parseNumericLike(l.vatAmount), 0);
  const re = doc.vatLines.reduce((s, l) => s + parseNumericLike(l.reAmount ?? 0), 0);
  const irpf = parseNumericLike(doc.irpfAmount);
  return { base, vat, re, irpf, expected: base + vat + re - irpf };
};

/**
 * Nombre del PDF para la descarga en ZIP:
 * `SERIE_NUMERO-DE-FACTURA_FECHA_CLIENTE.ext` (la serie solo si existe).
 * Máximo 120 caracteres: se recorta primero el nombre del cliente.
 */
export const buildIssuedFileName = (doc: IssuedProcessedFile): string => {
  const clean = (v: string) =>
    (v || "")
      .normalize("NFKC")
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/ /g, "_");

  const dot = doc.file.name.lastIndexOf(".");
  const ext = dot > 0 ? doc.file.name.slice(dot).toLowerCase() : ".pdf";

  const fecha = (doc.date || "").replace(/-/g, "") || "sinfecha";
  const serie = clean(doc.series);
  const numero = clean(doc.invoiceNumber) || "sinnumero";
  let cliente = clean(doc.customerName) || "cliente";

  const build = () => [serie, numero, fecha, cliente].filter(Boolean).join("_") + ext;
  const MAX = 120;
  if (build().length > MAX) {
    const excess = build().length - MAX;
    cliente = cliente.slice(0, Math.max(3, cliente.length - excess));
  }
  return build().slice(0, MAX);
};

/** Errores bloqueantes de un documento antes de exportar. */
export const validateIssuedDoc = (doc: IssuedProcessedFile): string[] => {
  const errors: string[] = [];
  if (!doc.date) errors.push("Falta la fecha");
  if (!doc.invoiceNumber.trim()) errors.push("Falta el nº de factura");
  // Las facturas simplificadas a cuenta de cliente genérico (43000000) no necesitan NIF.
  if (!doc.customerTaxId.trim() && !isGenericCustomerAccount(doc.customerAccount)) {
    errors.push("Falta el NIF del cliente");
  }
  if (!doc.customerAccount.trim()) errors.push("Falta la cuenta de cliente (430)");
  if (!doc.salesAccount.trim()) errors.push("Falta la cuenta de venta (700)");
  if (doc.vatLines.length === 0) errors.push("Sin desglose de IVA");
  const { expected } = docTotals(doc);
  if (Math.abs(expected - parseNumericLike(doc.totalAmount)) > 0.05) {
    errors.push(
      `Descuadre: base+IVA+RE−IRPF = ${expected.toFixed(2)} € y total = ${parseNumericLike(doc.totalAmount).toFixed(2)} €`,
    );
  }
  return errors;
};

export const toIssuedRow = (doc: IssuedProcessedFile): IssuedInvoiceRow => ({
  date: doc.date,
  series: doc.series || undefined,
  invoiceNumber: doc.invoiceNumber.trim(),
  customerName: doc.customerName.trim(),
  customerTaxId: doc.customerTaxId.trim(),
  customerAccount: doc.customerAccount.trim(),
  salesAccount: doc.salesAccount.trim(),
  vatLines: doc.vatLines.map((l) => ({
    taxableBase: parseNumericLike(l.taxableBase),
    vatRate: parseNumericLike(l.vatRate),
    vatAmount: parseNumericLike(l.vatAmount),
    reRate: l.reRate ? parseNumericLike(l.reRate) : undefined,
    reAmount: l.reAmount ? parseNumericLike(l.reAmount) : undefined,
  })),
  irpfRate: doc.irpfRate || null,
  irpfAmount: doc.irpfAmount || null,
  totalAmount: parseNumericLike(doc.totalAmount),
  postalCode: doc.postalCode || undefined,
  paymentMethod: doc.paymentMethod || undefined,
});

/**
 * Separa SERIE y NÚMERO conservando las letras que contengan.
 * Patrones admitidos:
 *  - Serie explícita: "Serie A2", "2", "FA".
 *  - Con separador: "1-260001", "A/0034", "FA1-260001", "2024/A-001".
 *  - Sin separador: "1260001" (serie de 1 dígito + número de 6-7 dígitos).
 */
/** Prefijo que en realidad es un año (2020–2035, o 20–35 en dos cifras) o ceros. */
const looksLikeYearOrZeros = (s: string): boolean =>
  /^(20[2-3]\d|2\d|3[0-5])$/.test(s) || /^0+$/.test(s);

export const splitSeriesAndNumber = (
  series: string,
  invoiceNumber: string,
  allowCompactSplit = false,
): { series: string; invoiceNumber: string } => {
  const raw = (invoiceNumber || "").trim().toUpperCase();
  let explicit = (series || "").trim().toUpperCase().replace(/^SERIE\s*[:.\-]?\s*/i, "").trim();
  // Una "serie" puramente numérica que es un año o ceros no es serie.
  if (/^\d+$/.test(explicit) && looksLikeYearOrZeros(explicit)) explicit = "";

  // 1) Prefijo corto (letras y/o dígitos) seguido de separador. Se conservan las letras.
  const withSep = raw.match(/^([A-Z]{0,4}\d{0,2}|[A-Z]{1,4})\s*[-/._\s]\s*(.+)$/);
  if (withSep && withSep[1]) {
    const prefix = withSep[1].trim();
    const numericOnly = /^\d+$/.test(prefix);
    if (!(numericOnly && looksLikeYearOrZeros(prefix))) {
      return { series: prefix, invoiceNumber: withSep[2].trim() };
    }
  }

  // 2) Bloque numérico compacto: solo en empresas con regla expresa de series (p. ej. Firmo).
  if (!explicit && allowCompactSplit) {
    const letters = raw.match(/^([A-Z]+)\s*/)?.[1] ?? "";
    const compact = raw.replace(/^[A-Z]+\s*/, "").match(/^(\d)(\d{6,7})$/);
    if (compact) return { series: `${letters}${compact[1]}`, invoiceNumber: compact[2] };
  }

  // 3) Sin prefijo detectable: se conserva la serie explícita tal cual.
  return { series: explicit, invoiceNumber: raw };
};


/** Serie deducida (compatibilidad). */
export const inferSeries = (series: string, invoiceNumber: string): string =>
  splitSeriesAndNumber(series, invoiceNumber).series;

/**
 * Reglas por empresa: la serie de la factura determina la cuenta de venta.
 * Clave = NIF de la empresa emisora (normalizado).
 */
const SERIES_SALES_ACCOUNT_RULES: Record<string, { bySeries: Record<string, string>; fallback: string }> = {
  // BODEGAS FIRMO, S.L. — serie 1 → 70500000; series 2, 3 y 4 → 70000000.
  B25936022: {
    bySeries: { "1": "70500000", "2": "70000000", "3": "70000000", "4": "70000000" },
    fallback: "70000000",
  },
};

/** True si la empresa tiene reglas de contrapartida por serie. */
export const hasSeriesRules = (companyTaxId: string): boolean =>
  Boolean(SERIES_SALES_ACCOUNT_RULES[normalizeNif(companyTaxId || "")]);

/** Cuenta de venta que corresponde a una serie, o null si la empresa no tiene regla. */
export const salesAccountForSeries = (companyTaxId: string, series: string): string | null => {
  const rule = SERIES_SALES_ACCOUNT_RULES[normalizeNif(companyTaxId || "")];
  if (!rule) return null;
  const key = (series || "").trim().toUpperCase();
  if (!key) return null; // sin serie identificada no forzamos ninguna cuenta
  if (rule.bySeries[key]) return rule.bySeries[key];
  const digits = key.replace(/\D/g, "");
  return rule.bySeries[digits] ?? rule.fallback;
};

/**
 * Empresas que NO usan serie: todo el identificador se trata como número de factura.
 * Clave = NIF de la empresa emisora (normalizado).
 */
const NO_SERIES_COMPANIES = new Set<string>([
  "B88135504", // HERMANOS GALLEGO ILLESCAS PATRIMONIAL SL
]);

/** True si la empresa no distingue serie y número de factura. */
export const skipsSeriesSplit = (companyTaxId: string): boolean =>
  NO_SERIES_COMPANIES.has(normalizeNif(companyTaxId || ""));

/** Normaliza serie/número y aplica la cuenta de venta asociada a esa serie. */
export const applySeriesRules = (doc: IssuedProcessedFile, companyTaxId: string): IssuedProcessedFile => {
  if (skipsSeriesSplit(companyTaxId)) {
    const s = (doc.series || "").trim();
    const n = (doc.invoiceNumber || "").trim();
    const full = s && !n.toUpperCase().startsWith(s.toUpperCase()) ? `${s}${n}` : n;
    // Solo el bloque anterior al guion (se descarta el año: "0001-2026" → "0001").
    const beforeDash = full.split(/[-/]/)[0].trim();
    return { ...doc, series: "", invoiceNumber: beforeDash || full };
  }

  const { series, invoiceNumber } = splitSeriesAndNumber(doc.series, doc.invoiceNumber, hasSeriesRules(companyTaxId));
  const account = salesAccountForSeries(companyTaxId, series);
  return { ...doc, series, invoiceNumber, salesAccount: account ?? doc.salesAccount };
};
